import { ChatContainer, ConversationHeader, MainContainer, Message, MessageInput, MessageInputProps, MessageList, MessageProps, TypingIndicator } from "@chatscope/chat-ui-kit-react";
import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { LLMHistory, LLMMessage, LLMRequest, LLMResponse, LLMResponseDTO, LLMSuggestionRequest, LLMSuggestionsResponseDTO } from "@dto/llm.dto";
import { DateUtil } from "@dto/util/DateUtil";
import { Button } from "antd";
import { AxiosError } from "axios";
import { FC, ReactElement, useContext, useEffect, useState } from "react";
import { LuHistory, LuMessageSquarePlus } from "react-icons/lu";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "src/data/Api";
import { Label } from "src/data/Label";
import { Log } from "src/service/log.service";
import { postApi } from "src/util/apiCalls";
import { GLOB } from "src/util/Glob";
import { ChatContext } from "./AIChat";

export const AIChatConversation: FC = props => {
  const { conversation, setConversation, setHistory, chatOpen, setHistoryOpen } = useContext(ChatContext);
  const [messages, setMessages] = useState<MessageProps[]>([]);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [suggestions, setSuggestions] = useState<LLMSuggestionsResponseDTO['data']>();
  const location = useLocation();
  const [lastLocation, setLastLocation] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!conversation) return;
    if (conversation.messages?.length === 0) {
      addSuggestions();
    } else {
      setMessages(conversation.messages.map(lm => ({ model: { position: 'single', message: lm.message, direction: lm.role === 'assistant' ? 'incoming' : 'outgoing' } })));
    }
  }, [conversation]);

  useEffect(() => {
    if (!chatOpen) return;
    if (!conversation) startNewConversation();
    const currentLocation = getLocation();
    if (conversation?.messages?.length < 1 && lastLocation !== currentLocation && messages?.length === 1) {
      setMessages([]);
      setTimeout(() => {
        addSuggestions();
      });
    }
  }, [chatOpen]);

  function getLocation() {
    return location.pathname + location.search + location.hash;
  }

  const onSendMessage: MessageInputProps['onSend'] = (html, content, text, nodes) => {
    setWaitingForResponse(true);
    setMessages(prev => [...prev, {
      model: {
        message: content,
        sentTime: new Date().toISOString(),
        sender: GLOB.userInfo.username,
        direction: 'outgoing',
        position: 'single'
      }
    }]);
    if (conversation.messages?.length < 1) {
      conversation.topic = content;
      setHistory(prev => [...prev, conversation]);
    }
    conversation.updated = DateUtil.getDateISO();
    conversation.messages.push({ message: content, role: 'user' });
    //setConversation({ ...conversation });
    const url = GLOB.selectedItem?.url || getLocation();
    const history: LLMMessage[] = messages.filter(m => m.model.message).map(m => ({ message: m.model.message, role: m.model.direction === 'incoming' ? 'assistant' : 'user' }));
    postApi<LLMResponseDTO, LLMRequest>(API_URL.LLM + '/chat', { message: content, url, history, topicId: conversation.topicId }).then((response) => {
      const lr = response.data.data;
      addAssistantMessage(lr);
      if (lr.url) {
        navigate(lr.url);
      }
    }, (reason: AxiosError<BasicResponseDTO>) => {
      Log.error('AI message failed!', reason);
      addAssistantNode(<div className="text-danger">{reason?.response?.data?.message || reason?.message}</div>);
    }).finally(() => setWaitingForResponse(false));
  };

  function addAssistantMessage(lr: LLMResponse, conv = conversation) {
    conv.messages.push({ message: lr.message, role: 'assistant' });
    conv.topic = lr.topic;
    conv.topicId = lr.topicId;
    setMessages(prev => [...prev, {
      model: {
        message: lr.message,
        sentTime: new Date().toISOString(),
        sender: Label.XORBOT,
        direction: 'incoming',
        position: 'single'
      }
    }]);
  }

  function addAssistantNode(node: ReactElement, conv = conversation) {
    setMessages(prev => [...prev, {
      model: {
        type: "custom",
        payload: <Message.CustomContent>{node}</Message.CustomContent>,
        sentTime: new Date().toISOString(),
        sender: Label.XORBOT,
        direction: 'incoming',
        position: 'single'
      }
    }]);
  }

  function buildSuggestionNode(sugo = suggestions) {
    return <div><h5>{sugo.message}</h5><div>{sugo.suggestions.map(s => <span key={s} className="xm-chat-suggestion">
      <a onClick={() => onSendMessage(null, s, null, null)}>{s}</a>&#32;
    </span>)}</div></div>;
  }

  function addSuggestions() {
    const currentLocation = getLocation();
    if (lastLocation === currentLocation) {
      addAssistantNode(buildSuggestionNode());
    } else {
      postApi<LLMSuggestionsResponseDTO, LLMSuggestionRequest>(API_URL.LLM + '/suggestions', { url: GLOB.selectedItem?.url || currentLocation }).then(resp => {
        setLastLocation(currentLocation);
        const sugo: LLMSuggestionsResponseDTO['data'] = resp?.data?.data;
        setSuggestions(sugo);
        addAssistantNode(buildSuggestionNode(sugo));
      }, reason => Log.error('Failed to get AI suggestions!', reason));
    }
  }

  const startNewConversation = () => {
    const newChat: LLMHistory = { topic: 'New chat', messages: [], updated: DateUtil.getDateISO() };
    setConversation(newChat);
    setMessages([]);
  };

  const showHistory = () => {
    setHistoryOpen(true);
  };

  return <MainContainer responsive>
    <ChatContainer>
      <ConversationHeader>
        <ConversationHeader.Content
          info={conversation?.updated}
          userName={conversation?.topic}
        />
        <ConversationHeader.Actions className="xm-chat-actions">
          <Button type="link" title="Start a new conversation" onClick={startNewConversation}><LuMessageSquarePlus /></Button>
          <Button type="link" title="Show conversations history" onClick={showHistory}><LuHistory /></Button>
        </ConversationHeader.Actions>
      </ConversationHeader>
      <MessageList typingIndicator={waitingForResponse ? <TypingIndicator content={`${Label.XORBOT} is thinking`} /> : null}>
        {messages.map(mp => <Message key={mp.model.sentTime} {...mp} />)}
      </MessageList>
      <MessageInput autoFocus attachButton={false} placeholder="Ask me about your infrastructure..." onSend={onSendMessage} />
    </ChatContainer>
  </MainContainer>;
};